import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { ArrowUpIcon, StopIcon, BroomIcon, SignOutIcon } from '@phosphor-icons/react';
import './Chat.css';
import { useTranscript, type RenderDirective } from '../lib/transcript';
import { MarkdownText } from '../lib/markdown';
import { getRenderComponent } from '../render-registry';

interface Props {
  onLogout: () => void;
}

function extractRender(data: unknown): RenderDirective | undefined {
  if (typeof data !== 'object' || data === null) return undefined;
  const d = data as Record<string, unknown>;
  if (typeof d.render !== 'string') return undefined;
  const props = typeof d.props === 'object' && d.props !== null ? (d.props as Record<string, unknown>) : {};
  return { key: d.render, props };
}

export function Chat({ onLogout }: Props) {
  const [transcript, addMessage, clearTranscript, updateMessage] = useTranscript();
  const [input, setInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [copiedAt, setCopiedAt] = useState<number | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  async function copyBubble(text: string, index: number) {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedAt(index);
      window.setTimeout(() => setCopiedAt((v) => (v === index ? null : v)), 1500);
    } catch {
      // clipboard API can fail in unfocused windows / sandboxed contexts;
      // silently swallow — user can retry.
    }
  }

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript]);

  // Autofocus the input on mount, but only on devices with a fine pointer
  // (desktop mouse). Skipping touch devices avoids popping the iOS keyboard
  // every time the PWA loads.
  useEffect(() => {
    if (window.matchMedia('(pointer: fine)').matches) {
      inputRef.current?.focus();
    }
  }, []);

  // Auto-mount the Workout panel on load if a session is active. We append
  // (not prepend) so it sits after any prior transcript — the freshest thing
  // in view is the panel. Guarded so it only fires once per page load.
  const autoMountedRef = useRef(false);
  useEffect(() => {
    if (autoMountedRef.current) return;
    autoMountedRef.current = true;
    let aborted = false;
    void (async () => {
      try {
        const res = await fetch('/api/tools/gym/active-session');
        if (!res.ok || aborted) return;
        const json = (await res.json()) as { payload?: Record<string, unknown> | null };
        if (aborted || !json.payload) return;
        addMessage({
          role: 'bilbo',
          content: 'Opening the workout panel.',
          at: Date.now(),
          render: { key: 'workout', props: json.payload as Record<string, unknown> },
        });
      } catch {
        // silent — auth not present yet, or network blip
      }
    })();
    return () => {
      aborted = true;
    };
  }, [addMessage]);

  // Keep the latest message in view when the keyboard opens/closes. Without
  // this the transcript shrinks but its scrollTop stays put, leaving the
  // newest bubble pushed just above the visible area. visualViewport.resize
  // fires after layout has settled, so the scroll lands in the right spot.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const handler = () => {
      bottomRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
    };
    vv.addEventListener('resize', handler);
    return () => vv.removeEventListener('resize', handler);
  }, []);

  // Mirror submitting state onto the html element so the gradient morphs.
  // No GradientBackground component, no prop threading — html.is-thinking
  // is read directly by the CSS.
  useEffect(() => {
    document.documentElement.classList.toggle('is-thinking', submitting);
    return () => {
      document.documentElement.classList.remove('is-thinking');
    };
  }, [submitting]);

  async function send(text: string) {
    addMessage({ role: 'user', content: text, at: Date.now() });
    const controller = new AbortController();
    abortRef.current = controller;
    setSubmitting(true);

    try {
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text }),
        signal: controller.signal,
      });
      const json = (await res.json().catch(() => null)) as
        | { display?: unknown; data?: unknown; tool?: string | null }
        | null;
      const display =
        typeof json?.display === 'string' ? json.display : 'Something amiss upstream.';

      // Suppression: when the workout panel is mounted and the LLM routed
      // to one of the mid-workout voice fallbacks, the panel will pick up
      // the DB change on its next poll. Don't double up with a text bubble.
      // The mutation still happened server-side; the panel handles display.
      const panelMounted = transcript.some((m) => m.render?.key === 'workout');
      const PANEL_OWNED_TOOLS = new Set([
        'setActiveExercise',
        'logExerciseSet',
        'skipExercise',
        'correctLastSet',
      ]);
      const suppress =
        panelMounted &&
        typeof json?.tool === 'string' &&
        PANEL_OWNED_TOOLS.has(json.tool);

      if (!suppress) {
        addMessage({
          role: 'bilbo',
          content: display,
          at: Date.now(),
          render: extractRender(json?.data),
        });
      }
      // Hint to any mounted panel that something may have changed,
      // so it can refetch immediately rather than waiting for its
      // 5-second poll tick.
      window.dispatchEvent(new Event('bilbo:gym-mutation'));
    } catch (err) {
      // Abort = user pressed stop; stay silent on the transcript.
      if ((err as { name?: string })?.name !== 'AbortError') {
        addMessage({
          role: 'bilbo',
          content: "The line's down. A minute?",
          at: Date.now(),
        });
      }
    } finally {
      abortRef.current = null;
      setSubmitting(false);
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) {
      abortRef.current?.abort();
      return;
    }
    if (!input.trim()) return;
    const text = input.trim();
    setInput('');
    void send(text);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as unknown as FormEvent);
    }
  }

  async function handleLogout() {
    abortRef.current?.abort();
    await fetch('/api/auth/logout', { method: 'POST' });
    clearTranscript();
    onLogout();
  }

  const sendDisabled = !submitting && !input.trim();

  return (
    <main className="chat-main">
      <header className="chat-header">
        <span className="chat-title">bilbo</span>
        <div className="chat-actions">
          <button
            type="button"
            onClick={clearTranscript}
            className="chat-icon-button"
            aria-label="tidy up"
          >
            <BroomIcon size={16} weight="regular" aria-hidden="true" />
            tidy up
          </button>
          <button
            type="button"
            onClick={handleLogout}
            className="chat-icon-button"
            aria-label="logout"
          >
            <SignOutIcon size={16} weight="regular" aria-hidden="true" />
            logout
          </button>
        </div>
      </header>

      <section className="chat-transcript">
        {transcript.length === 0 && <p className="chat-idle">Well?</p>}
        {transcript.map((m, i) => {
          if (m.render) {
            const Component = getRenderComponent(m.render.key);
            if (Component) {
              return (
                <div key={i} className="chat-bubble-render">
                  <Component
                    props={m.render.props}
                    onDismiss={(content) => {
                      updateMessage(i, content !== undefined ? { render: null, content } : { render: null });
                    }}
                    onComplete={(wrapUp) => {
                      updateMessage(i, { render: null });
                      addMessage({ role: 'bilbo', content: wrapUp, at: Date.now() });
                    }}
                  />
                </div>
              );
            }
          }
          const base = m.role === 'user' ? 'chat-bubble-user' : 'chat-bubble-bilbo';
          const className = copiedAt === i ? `${base} chat-bubble-copied` : base;
          return (
            <div
              key={i}
              className={className}
              onClick={() => void copyBubble(m.content, i)}
              role="button"
              tabIndex={0}
              aria-label="tap to copy"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  void copyBubble(m.content, i);
                }
              }}
            >
              {m.role === 'user' ? m.content : <MarkdownText text={m.content} />}
            </div>
          );
        })}
        <div ref={bottomRef} />
      </section>

      <form onSubmit={handleSubmit} className="chat-form">
        <div className="chat-input-wrap">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="…"
            rows={1}
            className="chat-input"
            aria-label="message"
            disabled={submitting}
          />
        </div>
        <button
          type="submit"
          disabled={sendDisabled}
          className={`chat-send${submitting ? ' stop' : ''}`}
          aria-label={submitting ? 'stop' : 'send'}
        >
          {submitting ? (
            <StopIcon size={18} weight="fill" aria-hidden="true" />
          ) : (
            <ArrowUpIcon size={18} weight="bold" aria-hidden="true" />
          )}
        </button>
      </form>
    </main>
  );
}
